import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { mockServices } from '../mock';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    serviceType: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleServiceChange = (value) => {
    setFormData({
      ...formData,
      serviceType: value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    // Validate serviceType
    if (!formData.serviceType) {
      setError('Please select a service type');
      setIsSubmitting(false);
      toast({
        title: "Validation Error",
        description: "Please select a service type",
        variant: "destructive"
      });
      return;
    }
    
    try {
      console.log('Submitting quote:', formData);
      // Submit to backend API
      const response = await axios.post(`${API}/quotes`, formData);
      
      console.log('Quote submitted successfully:', response.data);
      
      if (response.status === 201) {
        setIsSubmitted(true);
        toast({
          title: "Quote Request Submitted!",
          description: "We'll contact you shortly.",
        });
        
        setTimeout(() => {
          setFormData({
            name: '',
            phone: '',
            address: '',
            serviceType: '',
            message: ''
          });
          setIsSubmitted(false);
        }, 3000);
      }
    } catch (err) {
      console.error('Error submitting quote:', err);
      console.error('Error details:', err.response?.data);
      setError('Failed to submit quote. Please try again.');
      toast({
        title: "Submission Failed",
        description: err.response?.data?.detail || "Unable to submit your request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="quote" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="px-6 py-2 bg-black text-white rounded-full text-sm font-semibold tracking-wider uppercase inline-block mb-4">
            Free Consultation
          </span>
          <h2 className="text-5xl md:text-6xl font-extrabold text-black mb-6 tracking-tight">
            Get a Free Quote
          </h2>
          <p className="text-xl text-gray-600 font-light">
            Tell us about your project and we'll get back to you within 24 hours
          </p>
        </div>

        <Card className="border-0 shadow-2xl rounded-3xl overflow-hidden">
          <CardHeader className="bg-gradient-to-br from-gray-900 to-black text-white py-10">
            <CardTitle className="text-3xl font-extrabold tracking-tight">Request a Quote</CardTitle>
            <CardDescription className="text-gray-300 text-lg font-light">Fill in the details below and our team will contact you</CardDescription>
          </CardHeader>
          <CardContent>
            {isSubmitted ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-green-100 rounded-full mx-auto mb-6 flex items-center justify-center">
                  <CheckCircle className="w-14 h-14 text-green-600" />
                </div>
                <h3 className="text-3xl font-extrabold text-black mb-3">Thank You!</h3>
                <p className="text-gray-600 text-lg font-light">Your quote request has been submitted successfully.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6 p-8">
                {error && (
                  <div className="bg-red-50 border-l-4 border-red-600 rounded-xl p-5 flex items-start space-x-3">
                    <AlertCircle className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
                    <p className="text-red-800 font-medium">{error}</p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-semibold text-gray-700 tracking-wide">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    required
                    disabled={isSubmitting}
                    className="border-gray-200 focus:border-black rounded-xl py-6 px-4 text-base"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-semibold text-gray-700 tracking-wide">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 XXXXXXXXXX"
                    required
                    disabled={isSubmitting}
                    className="border-gray-200 focus:border-black rounded-xl py-6 px-4 text-base"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address" className="text-sm font-semibold text-gray-700 tracking-wide">Address *</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Enter your address"
                    required
                    disabled={isSubmitting}
                    className="border-gray-200 focus:border-black rounded-xl py-6 px-4 text-base"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="serviceType" className="text-sm font-semibold text-gray-700 tracking-wide">Service Type *</Label>
                  <Select 
                    value={formData.serviceType}
                    onValueChange={handleServiceChange} 
                    required 
                    disabled={isSubmitting}
                  >
                    <SelectTrigger className="border-gray-200 focus:border-black rounded-xl py-6 px-4 text-base">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {mockServices.map((service) => (
                        <SelectItem key={service.id} value={service.name}>
                          {service.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-sm font-semibold text-gray-700 tracking-wide">Project Description</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project requirements..."
                    rows={4}
                    disabled={isSubmitting}
                    className="border-gray-200 focus:border-black rounded-xl px-4 py-3 text-base resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-gray-900 to-black hover:from-black hover:to-gray-900 text-white text-lg py-7 rounded-full font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default QuoteForm;